import React from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as GreenDotSVG } from "go-list/core/images/statuses/active.svg";
import { ReactComponent as RedDotSVG } from "go-list/core/images/statuses/disabled.svg";
import { AvailableSubscriptionContentsApi } from "../../../../../../../../services/Api/Subscription/subscriptionTypes";
import { getParsedTermsNameToSnakeCase } from "../FormNew/services";
import { renderSubscriptionMoney } from "./utils";

interface Props {
	subscription: any;
	renderOnlyCurrentSubscription?: boolean;
	renderOnlyPreviousSubscription?: boolean;
	renderAll: boolean;
	config: AvailableSubscriptionContentsApi;
}

const SubscriptionSummaryPreviewSingleSubscriptionDetailsAddons = ({
	subscription,
	renderOnlyPreviousSubscription,
	renderOnlyCurrentSubscription,
	renderAll,
	config,
}: Props) => {
	const { t } = useTranslation();

	const isAddonDifferent = (addon: any) => {
		if (!addon.original) return true;
		if (!addon.estimate) return true;
		if (addon.original.quantity !== addon.estimate.quantity) return true;
		return false;
	};

	const findAddonTerms = (addon: any) => {
		const subscriptionConfig = config.contents.find(
			(content) => content.family_id === subscription.estimate.family_id
		);

		if (!subscriptionConfig) return undefined;

		const subscriptionPlan = subscriptionConfig.plans.find(
			({ plan_id }) => plan_id === subscription.estimate.plan_id
		);

		if (!subscriptionPlan) return undefined;

		const addonConfig = subscriptionPlan.addons.find(({ addon_id }) => addon_id === addon.addon_id);

		return addonConfig?.terms;
	};

	if (renderAll) {
		return subscription.addons.map((addon: any) => {
			const differentAddon = isAddonDifferent(addon);
			const isCancelledOriginal = differentAddon && !addon.original;
			const isCancelledEstimate = differentAddon && !addon.estimate;
			const isBetterEstimate = !!(
				differentAddon &&
				addon.estimate &&
				addon.original &&
				addon.estimate.quantity > addon.original.quantity
			);
			let originalClass = "";
			if (differentAddon) originalClass += " diff-subscription";
			if (isCancelledOriginal) originalClass += " cancelled";
			originalClass += " worse-subscription";
			let estimateClass = "";
			if (differentAddon) estimateClass += " diff-subscription";
			if (isCancelledEstimate) estimateClass += " cancelled";
			if (!isBetterEstimate && differentAddon) estimateClass += " worse-subscription";

			const addonTerms = findAddonTerms(addon);

			return (
				<div
					className={"row no-gutters"}
					key={`${subscription.family_id}_${addon.addon_id}_${addon.billing_period}`}
				>
					<div
						className={`col subscription-summary-list-item subscription-summary-list-item-addon original-app${originalClass}`}
					>
						{differentAddon && (
							<>
								{isBetterEstimate && <RedDotSVG className="me-1" />}
								{!isBetterEstimate && <GreenDotSVG className="me-1" />}
							</>
						)}
						<span>
							{addon.name}
							{addon.original && addon.original.quantity && (
								<>
									{" "}
									(<span className={"addon-quantity"}>x{addon.original.quantity}</span>)
								</>
							)}
						</span>
					</div>
					<div
						className={`col text-end subscription-summary-list-item subscription-summary-list-item-addon original-price${originalClass}`}
					>
						<div className={"subscription-summary-price text-muted"}>
							{addon.original &&
								renderSubscriptionMoney(addon.billing_period, addon.original.total_price)}
						</div>
					</div>
					<div
						className={`col subscription-summary-list-item subscription-summary-list-item-addon compare-app${estimateClass}`}
					>
						{differentAddon && (
							<>
								{!isBetterEstimate && <RedDotSVG className="me-1" />}
								{isBetterEstimate && <GreenDotSVG className="me-1" />}
							</>
						)}
						<span>
							{addon.name}
							{addon.estimate && addon.estimate.quantity && (
								<>
									{" "}
									(<span className={"addon-quantity"}>x{addon.estimate.quantity}</span>)
								</>
							)}
							{addonTerms && (
								<a target="_blank" href={addonTerms.url} rel="noreferrer" className="ms-1">
									{t(
										`enums.subscriptions.addon.terms.${getParsedTermsNameToSnakeCase(
											addonTerms.name
										)}`
									)}
								</a>
							)}
						</span>
					</div>
					<div
						className={`col text-end subscription-summary-list-item subscription-summary-list-item-addon compare-price${estimateClass}`}
					>
						<div className={"subscription-summary-price text-muted"}>
							{addon.estimate &&
								renderSubscriptionMoney(addon.billing_period, addon.estimate.total_price)}
						</div>
					</div>
				</div>
			);
		});
	}

	if (renderOnlyPreviousSubscription) {
		return subscription.addons.map((addon: any) => {
			const differentAddon = isAddonDifferent(addon);
			const isCancelledOriginal = differentAddon && !addon.original;
			const isBetterEstimate = !!(
				differentAddon &&
				addon.estimate &&
				addon.original &&
				addon.estimate.quantity > addon.original.quantity
			);
			let originalClass = "";
			if (differentAddon) originalClass += " diff-subscription";
			if (isCancelledOriginal) originalClass += " cancelled";
			originalClass += " worse-subscription";
			return (
				<div
					className={"row no-gutters"}
					key={`${subscription.family_id}_${addon.addon_id}_${addon.billing_period}`}
				>
					<div
						className={`col subscription-summary-list-item subscription-summary-list-item-addon original-app${originalClass}`}
					>
						{differentAddon && (
							<>
								{isBetterEstimate && <RedDotSVG className="me-1" />}
								{!isBetterEstimate && <GreenDotSVG className="me-1" />}
							</>
						)}
						<span>
							{addon.name}
							{addon.original && addon.original.quantity && (
								<>
									{" "}
									(<span className={"addon-quantity"}>x{addon.original.quantity}</span>)
								</>
							)}
						</span>
					</div>
					<div
						className={`col text-end subscription-summary-list-item subscription-summary-list-item-addon original-price${originalClass}`}
					>
						<div className={"subscription-summary-price text-muted"}>
							{addon.original &&
								renderSubscriptionMoney(addon.billing_period, addon.original.total_price)}
						</div>
					</div>
				</div>
			);
		});
	}

	if (renderOnlyCurrentSubscription) {
		return subscription.addons.map((addon: any) => {
			const differentAddon = isAddonDifferent(addon);
			const isCancelledEstimate = differentAddon && !addon.estimate;
			const isBetterEstimate = !!(
				differentAddon &&
				addon.estimate &&
				addon.original &&
				addon.estimate.quantity > addon.original.quantity
			);
			let estimateClass = "";
			if (differentAddon) estimateClass += " diff-subscription";
			if (isCancelledEstimate) estimateClass += " cancelled";
			if (!isBetterEstimate && differentAddon) estimateClass += " worse-subscription";

			const addonTerms = findAddonTerms(addon);

			return (
				<div
					className={"row no-gutters"}
					key={`${subscription.family_id}_${addon.addon_id}_${addon.billing_period}`}
				>
					<div
						className={`col subscription-summary-list-item subscription-summary-list-item-addon compare-app${estimateClass}`}
					>
						{differentAddon && (
							<>
								{!isBetterEstimate && <RedDotSVG className="me-1" />}
								{isBetterEstimate && <GreenDotSVG className="me-1" />}
							</>
						)}
						<span>
							{addon.name}
							{addon.estimate && addon.estimate.quantity && (
								<>
									{" "}
									(<span className={"addon-quantity"}>x{addon.estimate.quantity}</span>)
								</>
							)}
							{addonTerms && (
								<a target="_blank" href={addonTerms.url} rel="noreferrer" className="ms-1">
									{t(
										`enums.subscriptions.addon.terms.${getParsedTermsNameToSnakeCase(
											addonTerms.name
										)}`
									)}
								</a>
							)}
						</span>
					</div>
					<div
						className={`col text-end subscription-summary-list-item subscription-summary-list-item-addon compare-price${estimateClass}`}
					>
						<div className={"subscription-summary-price text-muted"}>
							{addon.estimate &&
								renderSubscriptionMoney(addon.billing_period, addon.estimate.total_price)}
						</div>
					</div>
				</div>
			);
		});
	}
};

export default SubscriptionSummaryPreviewSingleSubscriptionDetailsAddons;
